import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * Fetch-once admin data hook (StrictMode-safe, no duplicate requests).
 * Returns items (never null for mapping), a stable `loading` flag, `error`,
 * and `reload` for refetching after mutations. Refetches when `endpoint` changes.
 */
export function useAdminData(endpoint) {
  const [data, setData] = useState(null); // null = loading
  const [error, setError] = useState(false);
  const fetchedRef = useRef("");

  const load = useCallback(() => {
    return api.get(endpoint).then((r) => setData(r.data)).catch(() => setError(true));
  }, [endpoint]);

  useEffect(() => {
    if (fetchedRef.current === endpoint) return; // prevents StrictMode double-fetch
    fetchedRef.current = endpoint;
    setData(null);
    setError(false);
    load();
  }, [endpoint, load]);

  return { items: data || [], loading: data === null && !error, error, reload: load, setItems: setData };
}

/** Skeleton rows that match the table's column count — keeps layout stable while loading. */
export function TableSkeletonRows({ cols, rows = 6 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="border-t border-border" aria-hidden="true">
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="p-4">
              <div
                className="h-4 rounded bg-secondary animate-pulse"
                style={{ width: j === cols - 1 ? "2.5rem" : `${55 + ((j * 13) % 35)}%`, marginLeft: j === cols - 1 ? "auto" : 0 }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
